import {
  type ApiHolder,
  type ConfigApi,
  configApiRef,
  type DiscoveryApi,
  discoveryApiRef,
  type FetchApi,
  fetchApiRef,
  microsoftAuthApiRef,
  type OAuthApi,
  useApi,
} from '@backstage/core-plugin-api';

import { armAssertionSupplier } from './assertion';
import type { QueryResourcesQueryDependencies } from './query-resources-options';

/** A Backstage API that a Resource Graph request needs was not available. */
export class ArmTransportUnavailableError extends Error {
  /**
   * @param apiName - The API that was absent, in words, such as `Discovery API`.
   */
  constructor(apiName: string) {
    super(`Resource Graph requests require the ${apiName}.`);
    this.name = 'ArmTransportUnavailableError';
  }
}

function buildArmTransport(apis: {
  readonly configApi: Pick<ConfigApi, 'getOptionalString'>;
  readonly discoveryApi: DiscoveryApi;
  readonly fetchApi: Pick<FetchApi, 'fetch'>;
  readonly microsoftAuthApi: Pick<OAuthApi, 'getAccessToken'>;
}): QueryResourcesQueryDependencies {
  return {
    discoveryApi: apis.discoveryApi,
    fetch: apis.fetchApi.fetch,
    getAssertion: armAssertionSupplier({
      configApi: apis.configApi,
      microsoftAuthApi: apis.microsoftAuthApi,
    }),
  };
}

/**
 * Reads the Resource Graph transport from Backstage's APIs, for code outside React such as a submit validator.
 *
 * @param apiHolder - The APIs Backstage hands a validator in `context.apiHolder`.
 * @returns The transport every Resource Graph request of these pickers needs.
 * `getAssertion` is `null` when `microsoftApis.obo.clientId` is not configured.
 * @throws {@link ArmTransportUnavailableError} when one of the four APIs is
 * absent, naming the first in the order discovery, fetch, config, Microsoft auth.
 */
export function readArmTransport(
  apiHolder: ApiHolder,
): QueryResourcesQueryDependencies {
  const discoveryApi = apiHolder.get(discoveryApiRef);
  const fetchApi = apiHolder.get(fetchApiRef);
  const configApi = apiHolder.get(configApiRef);
  const microsoftAuthApi = apiHolder.get(microsoftAuthApiRef);
  if (discoveryApi === undefined)
    throw new ArmTransportUnavailableError('Discovery API');
  if (fetchApi === undefined)
    throw new ArmTransportUnavailableError('Fetch API');
  if (configApi === undefined)
    throw new ArmTransportUnavailableError('Config API');
  if (microsoftAuthApi === undefined)
    throw new ArmTransportUnavailableError('Microsoft auth API');
  return buildArmTransport({
    configApi,
    discoveryApi,
    fetchApi,
    microsoftAuthApi,
  });
}

/**
 * The Resource Graph transport for a component, from Backstage's APIs.
 *
 * @returns The transport every Resource Graph request of these pickers needs.
 * `getAssertion` is `null` when `microsoftApis.obo.clientId` is not configured.
 */
export function useArmTransport(): QueryResourcesQueryDependencies {
  const configApi = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const microsoftAuthApi = useApi(microsoftAuthApiRef);
  return buildArmTransport({
    configApi,
    discoveryApi,
    fetchApi,
    microsoftAuthApi,
  });
}
